import { AddOutlined, EditOutlined } from "@mui/icons-material";
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select,
  Stack, TextField, Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BusinessPartner, createBusinessPartner, loadBusinessPartners,
  updateBusinessPartner
} from "./api";

type Draft = {
  id?: string; code: string; displayName: string;
  kind: "INTERNAL_TEAM" | "OTHER" | "CLIENT" | "SUPPLIER";
  status: "ACTIVE" | "INACTIVE"; version: number; memberIds: string[];
};

const emptyDraft: Draft = {
  code: "", displayName: "", kind: "INTERNAL_TEAM",
  status: "ACTIVE", version: 0, memberIds: []
};

export function BusinessPartnerSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["business-partners"], queryFn: loadBusinessPartners
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const save = useMutation({
    mutationFn: async (value: Draft) => value.id
      ? updateBusinessPartner({
          ...query.data!.businessPartners.find((partner) => partner.id === value.id)!,
          code: value.code, displayName: value.displayName, status: value.status,
          version: value.version,
          members: query.data!.users.filter((user) => value.memberIds.includes(user.id))
            .map((user) => ({ ...user, role: "DECISION_MAKER" }))
        })
      : createBusinessPartner({
          code: value.code, displayName: value.displayName,
          kind: value.kind as "INTERNAL_TEAM" | "OTHER", memberIds: value.memberIds
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-partners"] });
      queryClient.invalidateQueries({ queryKey: ["workflow-configuration"] });
      setDraft(null);
    }
  });
  const edit = (partner: BusinessPartner) => setDraft({
    id: partner.id, code: partner.code, displayName: partner.displayName,
    kind: partner.kind, status: partner.status, version: partner.version,
    memberIds: partner.members.map((member) => member.id)
  });

  return <Stack spacing={1.5}>
    <Alert severity="info">{t("configuration.businessPartners.description")}</Alert>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h6" fontWeight={800}>
        {t("configuration.businessPartners.title")}
      </Typography>
      <Button variant="contained" startIcon={<AddOutlined />}
        onClick={() => setDraft({ ...emptyDraft })}>
        {t("configuration.businessPartners.new")}
      </Button>
    </Stack>
    {query.isError && <Alert severity="error">
      {t("configuration.businessPartners.loadError")}
    </Alert>}
    {query.data?.businessPartners.map((partner) => <Card key={partner.id} variant="outlined">
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
          alignItems={{ sm: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800}>{partner.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {partner.code} · {t(`configuration.businessPartners.kinds.${partner.kind}`)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.businessPartners.decisionMakers", { count: partner.members.length })}
            </Typography>
          </Box>
          <Chip size="small" color={partner.status === "ACTIVE" ? "success" : "default"}
            label={partner.status === "ACTIVE"
              ? t("configuration.businessPartners.active")
              : t("configuration.businessPartners.inactive")} />
          <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => edit(partner)}>
            {t("configuration.common.configure")}
          </Button>
        </Stack>
      </CardContent>
    </Card>)}
    <BusinessPartnerDialog draft={draft} users={query.data?.users ?? []}
      saving={save.isPending} error={save.isError}
      onClose={() => setDraft(null)} onSave={(value) => save.mutate(value)} />
  </Stack>;
}

function BusinessPartnerDialog({ draft, users, saving, error, onClose, onSave }: {
  draft: Draft | null;
  users: Array<{ id: string; displayName: string; email: string }>;
  saving: boolean; error: boolean; onClose: () => void; onSave: (draft: Draft) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState<Draft>(emptyDraft);
  useEffect(() => { if (draft) setValue(draft); }, [draft]);
  const linked = value.kind === "CLIENT" || value.kind === "SUPPLIER";
  return <Dialog open={Boolean(draft)} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{value.id
      ? t("configuration.businessPartners.edit")
      : t("configuration.businessPartners.create")}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      {error && <Alert severity="error">{t("configuration.common.operationError")}</Alert>}
      <TextField label={t("configuration.businessPartners.code")} value={value.code}
        onChange={(event) => setValue({ ...value, code: event.target.value })} />
      <TextField label={t("configuration.businessPartners.name")} value={value.displayName}
        onChange={(event) => setValue({ ...value, displayName: event.target.value })} />
      <FormControl disabled={Boolean(value.id)}>
        <InputLabel>{t("configuration.businessPartners.kind")}</InputLabel>
        <Select value={value.kind} label={t("configuration.businessPartners.kind")}
          onChange={(event) => setValue({ ...value, kind: event.target.value as Draft["kind"] })}>
          {value.kind === "CLIENT" && <MenuItem value="CLIENT">
            {t("configuration.businessPartners.kinds.CLIENT")}
          </MenuItem>}
          {value.kind === "SUPPLIER" && <MenuItem value="SUPPLIER">
            {t("configuration.businessPartners.kinds.SUPPLIER")}
          </MenuItem>}
          <MenuItem value="INTERNAL_TEAM">{t("configuration.businessPartners.kinds.INTERNAL_TEAM")}</MenuItem>
          <MenuItem value="OTHER">{t("configuration.businessPartners.kinds.OTHER")}</MenuItem>
        </Select>
      </FormControl>
      {value.id && <FormControl><InputLabel>{t("configuration.businessPartners.status")}</InputLabel>
        <Select value={value.status} label={t("configuration.businessPartners.status")}
          onChange={(event) => setValue({ ...value, status: event.target.value as Draft["status"] })}>
          <MenuItem value="ACTIVE">{t("configuration.businessPartners.active")}</MenuItem>
          <MenuItem value="INACTIVE">{t("configuration.businessPartners.inactive")}</MenuItem>
        </Select></FormControl>}
      <FormControl>
        <InputLabel>{t("configuration.businessPartners.members")}</InputLabel>
        <Select multiple value={value.memberIds}
          label={t("configuration.businessPartners.members")}
          onChange={(event) => setValue({ ...value,
            memberIds: typeof event.target.value === "string"
              ? event.target.value.split(",") : event.target.value })}>
          {users.map((user) => <MenuItem key={user.id} value={user.id}>
            {user.displayName} · {user.email}
          </MenuItem>)}
        </Select>
      </FormControl>
      {linked && <Alert severity="info">{t("configuration.businessPartners.linked")}</Alert>}
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose}>{t("configuration.common.cancel")}</Button>
      <Button variant="contained" disabled={saving || !value.code.trim() || !value.displayName.trim()}
        onClick={() => onSave(value)}>{t("configuration.common.save")}</Button>
    </DialogActions>
  </Dialog>;
}
